import React from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Link } from "react-router-dom";
import { FileSignature, MessageSquare, FolderOpen, Phone, ExternalLink, ArrowRight, Send, MessageCircle, Mail } from "lucide-react";
import { cn } from "../../lib/utils";
import { Hint } from "./PaymentBits";
import { tr } from "./i18n";
import {
  fmtDate, parseDay, today, daysBetween, days, PROJECT_STATUS_UK, initials,
} from "./format";

// Messenger links from a staff card: Telegram username wins over phone.
const contactLinks = (person) => {
  if (!person) return [];
  const links = [];
  const phone = (person.phone || "").replace(/[\s\-()]/g, "");
  const tg = (person.telegram || "").trim().replace(/^@/, "").replace(/^https?:\/\/t\.me\//, "");
  if (tg) links.push({ label: "Telegram", href: `https://t.me/${tg}` });
  else if (phone) links.push({ label: "Telegram", href: `https://t.me/${phone}` });
  if (phone) links.push({ label: "WhatsApp", href: `https://wa.me/${phone.replace(/^\+/, "")}` });
  if (person.email) links.push({ label: "Email", href: `mailto:${person.email}` });
  return links;
};

// One compact line per person: avatar · name · role (duty in a tooltip) ·
// contact icons. No second/third text lines.
const ICONS = { Telegram: Send, WhatsApp: MessageCircle, Email: Mail };
const PersonRow = ({ person, role, duty, accent }) => (
  <div className="flex items-center gap-2.5 min-w-0 py-1">
    <div className={cn(
      "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold",
      accent ? "bg-orange-500/15 text-orange-500" : "bg-zinc-800 text-zinc-300",
    )}>
      {initials(person.name)}
    </div>
    <p className="text-sm text-zinc-100 font-medium truncate min-w-0">
      {person.name}
      <span className="font-normal text-zinc-500"> · {role}</span>
    </p>
    {duty && <Hint text={duty} />}
    <div className="ml-auto flex items-center gap-1 flex-shrink-0">
      {person.phone && (
        <Hint text={person.phone}>
          <a href={`tel:${person.phone}`} aria-label={person.phone}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:text-orange-500 hover:bg-zinc-800">
            <Phone className="h-3.5 w-3.5" />
          </a>
        </Hint>
      )}
      {contactLinks(person).map((l) => {
        const Icon = ICONS[l.label] || ExternalLink;
        return (
          <Hint key={l.label} text={l.label}>
            <a href={l.href} target="_blank" rel="noopener noreferrer" aria-label={l.label}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:text-orange-500 hover:bg-zinc-800">
              <Icon className="h-3.5 w-3.5" />
            </a>
          </Hint>
        );
      })}
    </div>
  </div>
);

const Fact = ({ label, children, hint, tip }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-semibold flex items-center gap-1">
      {label}
      {tip && <Hint text={tip} />}
    </p>
    <p className="text-sm text-zinc-100 font-medium mt-0.5 truncate">
      {children}
      {hint && <span className="text-xs font-normal text-zinc-500"> · {hint}</span>}
    </p>
  </div>
);

/**
 * Project passport — the same facts the internal project card shows, in plain
 * words: contract, dates, how fast the client should answer, and who does what
 * on both sides (team variant C: two sides + one line of responsibility).
 */
const ProjectPassport = ({ project, clientContacts = [], tasksDone = 0, tasksTotal = 0, linkToPage = false }) => {
  if (!project) return null;
  const contract = project.contract || {};
  const start = parseDay(project.start_date);
  const end = parseDay(project.deadline);
  const now = today();
  const done = project.status === "completed";

  let timeline = null;
  if (start && end && end > start) {
    const total = daysBetween(start, end);
    const passed = Math.min(Math.max(daysBetween(start, now), 0), total);
    timeline = { pct: Math.round((passed / total) * 100), left: daysBetween(now, end) };
  } else if (end) {
    timeline = { pct: null, left: daysBetween(now, end) };
  }

  let deadlineHint = null;
  if (done) deadlineHint = tr("Проєкт здано");
  else if (timeline) {
    deadlineHint =
      timeline.left > 0
        ? `${tr("залишилось")} ${days(timeline.left)}`
        : timeline.left === 0
          ? tr("здача сьогодні")
          : tr("минула");
  }

  const contractText = contract.number
    ? `№ ${contract.number}`
    : contract.date
      ? tr("Підписано")
      : tr("Готується");

  const responseHours = project.response_time_hours;
  const ourSide = [
    project.project_manager && {
      person: project.project_manager,
      role: tr("менеджер проєкту"),
      duty: tr("Ваш головний контакт: хід робіт, правки, терміни"),
      accent: true,
    },
    project.sales_manager &&
      project.sales_manager.name !== project.project_manager?.name && {
        person: project.sales_manager,
        role: tr("менеджер з продажу"),
        duty: tr("Договір, рахунки, нові задачі"),
      },
  ].filter(Boolean);

  const taskPct = tasksTotal > 0 ? Math.round((tasksDone / tasksTotal) * 100) : null;
  const linkCls = "inline-flex items-center gap-1.5 rounded-md border border-zinc-800 px-2 py-1 text-xs text-zinc-300 hover:border-orange-500/50";
  return (
    <Card data-testid="client-project-passport" className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Title row: name · status · page link */}
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-extrabold text-zinc-50 font-['Manrope'] tracking-tight min-w-0 truncate">
            {linkToPage ? (
              <Link to={`/client/projects/${project.id}`} className="hover:text-orange-500 transition-colors">{project.name}</Link>
            ) : project.name}
          </h2>
          <Badge className={cn(
            "gap-1.5 font-medium",
            done ? "bg-green-500/15 text-green-500 border-green-500/30"
              : project.status === "awaiting_client" || project.status === "awaiting_payment" ? "bg-amber-500/15 text-amber-500 border-amber-500/30"
              : "bg-orange-500/15 text-orange-500 border-orange-500/30",
          )}>
            {tr(PROJECT_STATUS_UK[project.status] || "В роботі")}
          </Badge>
          {linkToPage && (
            <Link to={`/client/projects/${project.id}`} data-testid="client-open-project-page"
              className="ml-auto inline-flex items-center gap-1 text-xs text-orange-500 hover:text-orange-400 font-medium">
              {tr("Відкрити проєкт")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>

        {/* Facts in one row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-3">
          <Fact label={tr("Договір")} hint={contract.date ? fmtDate(contract.date) : null}>{contractText}</Fact>
          <Fact label={tr("Старт")}>{fmtDate(project.start_date) || "—"}</Fact>
          <Fact
            label={tr("Здача")}
            tip={timeline && timeline.left < 0 && !done ? tr("Запланована дата здачі минула — актуальний строк уточнює ваш менеджер проєкту.") : null}
          >
            {fmtDate(project.deadline) || "—"}
            {deadlineHint && <span className={cn("text-xs font-normal", timeline?.left < 0 && !done ? "text-amber-500" : "text-zinc-500")}> · {deadlineHint}</span>}
          </Fact>
          <Fact
            label={tr("Ваша відповідь")}
            tip={responseHours ? tr("Скільки часу у вас є, щоб відповісти на наш запит чи погодити роботу.") : null}
          >
            {responseHours ? `${tr("до")} ${responseHours} ${tr("год")}` : "—"}
          </Fact>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">{tr("Задачі")}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-zinc-100 font-medium tabular-nums">{tasksTotal > 0 ? `${tasksDone}/${tasksTotal}` : "—"}</span>
              {taskPct != null && (
                <div className="h-1.5 flex-1 rounded-full bg-zinc-800 overflow-hidden">
                  <div className="h-full rounded-full bg-orange-500" style={{ width: `${taskPct}%` }} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Team (both sides) + links — one compact band */}
        <div className="border-t border-zinc-800 pt-3 grid gap-x-8 gap-y-1 md:grid-cols-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-semibold mb-0.5">{tr("З нашого боку")}</p>
            {ourSide.length > 0 ? ourSide.map((m) => <PersonRow key={m.role} {...m} />)
              : <p className="text-sm text-zinc-500 py-1">{tr("Менеджера призначимо найближчим часом")}</p>}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-semibold mb-0.5">{tr("З вашого боку")}</p>
            {clientContacts.length > 0 ? clientContacts.slice(0, 3).map((c, i) => (
              <PersonRow key={`${c.name}-${i}`} person={{ name: c.name }}
                role={c.role === "contact_person" ? tr("контактна особа") : tr("доступ до кабінету")}
                duty={c.role === "contact_person" ? tr("Погоджує роботи й отримує рахунки") : c.email} />
            )) : <p className="text-sm text-zinc-500 py-1">{tr("Контакт не вказано")}</p>}
            {(project.communication_group || project.materials_link || (contract.link && /^https?:\/\//.test(contract.link))) && (
              <div className="flex flex-wrap gap-1.5 pt-2">
                {project.communication_group && (
                  <a href={project.communication_group} target="_blank" rel="noopener noreferrer" className={linkCls}>
                    <MessageSquare className="h-3.5 w-3.5 text-orange-500" /> {tr("Чат проєкту")}
                  </a>
                )}
                {project.materials_link && (
                  <a href={project.materials_link} target="_blank" rel="noopener noreferrer" className={linkCls}>
                    <FolderOpen className="h-3.5 w-3.5 text-orange-500" /> {tr("Матеріали")}
                  </a>
                )}
                {contract.link && /^https?:\/\//.test(contract.link) && (
                  <a href={contract.link} target="_blank" rel="noopener noreferrer" className={linkCls}>
                    <FileSignature className="h-3.5 w-3.5 text-orange-500" /> {tr("Договір")}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectPassport;
